import { Spinner } from "@blueprintjs/core";
import React, { useEffect } from "react";

import { usePagination } from "../../hooks/use-pagination";
import { usePlaybookContext } from "../../pages/Playbook/playbook-context";
import { DEFAULT_PLAY_PAGE_SIZE } from "../../redux/constants";
import { useGetPlays } from "../../redux/slices";
import { SportPlay } from "../../types";
import { Button } from "../Button/button";
import { PlayTableFilters } from "../play-table-filters/play-table-filters";
import { Table, TableFooter, TableSkeleton } from "./components";
import { usePlaysWithSelection, useTableFilters } from "./hooks";
import styles from "./play-table.module.scss";
import { getFieldSearch } from "./utils";

interface PlayTableProps {
  activePlayId: string | undefined;
  setActivePlayId: (id: string) => void;
  openPlayTab: () => void;
  openEditPlayDetails: (play: SportPlay) => void;
  creatingPlay: boolean;
  handleClickCreatePlay: () => void;
  playbooksPage?: boolean;
}

export const PlayTable = ({
  activePlayId,
  setActivePlayId,
  openPlayTab,
  openEditPlayDetails,
  creatingPlay,
  handleClickCreatePlay,
  playbooksPage = false,
}: PlayTableProps) => {
  const tableFilters = useTableFilters();
  const { seasonMultiSelect, teamsMultiSelect, seriesMultiSelect, tagsMultiSelect, playNameFilterDebounced, sort } =
    tableFilters;

  const fieldSearch = getFieldSearch({
    seasonMultiSelect,
    teamsMultiSelect,
    seriesMultiSelect,
    tagsMultiSelect,
    playNameFilter: playNameFilterDebounced,
  });

  const pagination = usePagination({ defaultPageSize: DEFAULT_PLAY_PAGE_SIZE });
  const { page, pageSize } = pagination;

  const { data: pagedData, isFetching } = useGetPlays({ page, pageSize, fieldSearch, sort: sort || undefined });

  const data = pagedData?.data;
  const totalRows = pagedData?.totalRows || 0;

  useEffect(() => {
    if (data && data?.length > 0 && !activePlayId && !playbooksPage) setActivePlayId(data[0]?.id || "");
  }, [data, setActivePlayId, activePlayId, playbooksPage]);

  const { setPlaysToAdd, addingPlay, selectedPlaybookPlayIds, playsToAdd } = usePlaybookContext();

  const playsWithSelection = usePlaysWithSelection({
    plays: data,
    activePlayId,
    playbooksPage,
    selectedPlaybookPlayIds,
  });

  const { selectedIds, setSelectedIds } = playsWithSelection;

  // so when addingPlay gets toggled selected plays don't persist
  useEffect(() => {
    if (setPlaysToAdd && addingPlay) {
      setPlaysToAdd(selectedIds);
    }

    if (!addingPlay && playsToAdd.length > 0) {
      setSelectedIds([]);
      setPlaysToAdd([]);
    }
  }, [addingPlay, selectedIds, setPlaysToAdd, setSelectedIds]);

  return (
    <div className={styles.playTable}>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <PlayTableFilters {...tableFilters} />
        <div className={styles.newPlayContainer}>
          <Button onClick={handleClickCreatePlay} disabled={creatingPlay} id="createPlayButton">
            {creatingPlay ? <Spinner size={20} /> : "Create Play"}
          </Button>
        </div>
      </div>
      <div className={styles.bodyTableContainer}>
        {isFetching || !data ? (
          <TableSkeleton />
        ) : (
          <Table
            data={data}
            activePlayId={activePlayId}
            setActivePlayId={setActivePlayId}
            openPlayTab={openPlayTab}
            openEditPlayDetails={openEditPlayDetails}
            {...playsWithSelection}
            sortOnField={tableFilters.sortOnField}
            sort={tableFilters.sort}
            playbooksPage={playbooksPage}
          />
        )}
      </div>
      <TableFooter {...pagination} totalRows={totalRows} />
    </div>
  );
};
